import React, { useState } from 'react';
import {
  Box,
  ButtonBase,
  Button,
  Collapse,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  ExpandMore as ExpandMoreIcon,
  InfoOutlined as InfoOutlinedIcon,
} from '@mui/icons-material';

import { alpha } from '@mui/material/styles';

import { logAnalyticsEvent } from '../../utils/analytics';
import { formatFrequencyCompact, buildDosageTooltip, getDisplayDosage } from '../../utils/dosageDisplay';
import InteractionDetailsModal from './InteractionDetailsModal';
import StackDisclaimer from './StackDisclaimer';
import RiskChip, { riskChipLevel } from '../stack/RiskChip';
import { r, color, radius, motion, tabularNums } from '../../theme/tokens';

/**
 * A stack the user built — the "My stacks card" column of §3 in
 * `Brand_Design/myStack Dashboard - Engineering Handoff.html`.
 *
 * §3's premise is that recommended and built cards share one anatomy and the
 * differences are *listed, not implied*. So this card and
 * `Dashboard/components/RecommendedStackCard` are deliberately the same shape:
 * container, title row, meta, rows, overflow line, footer. What differs is only
 * what §3 lists — no accent bar, no "Recommended" badge, no context block, and
 * a footer carrying the Public/Private switch and View · Edit · Delete instead
 * of Pin.
 *
 * Two rules that are load-bearing rather than cosmetic:
 *
 *   - **RiskChips never collapse** (§8). On mobile the supplement rows fold
 *     into an accordion, so the risk signal is lifted into the header row,
 *     which is always visible. Interaction risk is never behind a tap. This is
 *     the one thing an accordion would break by accident, so the chip is
 *     rendered outside the `Collapse` rather than inside it.
 *   - **No shadow** (§10). Hairlines separate cards. The card's previous three
 *     shadow variants — and the gradient header that carried them — are gone.
 *
 * There is no regeneration overlay here any more. The old card carried an
 * absolutely-positioned one, which broke the frontend's no-absolute rule
 * (UI/react/stack/CLAUDE.md rule 2) — but the real problem was that it had
 * become unreachable: My stacks holds only user-built stacks now, and a
 * recommended stack that is rebuilding shows the synthesis mark in
 * `RecommendedStackCard`'s GENERATING state instead. Deleted rather than
 * re-laid-out, so the dead branch went with the rule violation.
 *
 * The whole surface reads from `theme/tokens`; this file previously carried 33
 * raw hexes, 7 risk-coloured decorations and 8 px literals, which is what the
 * design-system sweep in STACK-515 was measuring.
 */

const REDUCED = '@media (prefers-reduced-motion: reduce)';
const MAX_ROWS = 4;

const getDisplayName = (stack) => {
  if (stack.isSystemGenerated) {
    if (stack.name === 'Medication Support') return 'Replenish Stack';
    if (stack.name === 'Diet Support') return 'Nourish Stack';
  }
  return stack.name;
};

/**
 * §3's meta line for a built card: "{n} supplements · {goal} · Updated {date}".
 * Segments the stack does not have are dropped rather than rendered empty —
 * a trailing "· " with nothing after it reads as a bug.
 */
const buildMetaLine = (stack, formatDate) => {
  const count = Array.isArray(stack.supplements) ? stack.supplements.length : 0;
  const segments = [`${count} supplement${count === 1 ? '' : 's'}`];
  const goal = stack.healthGoal || stack.goal;
  if (goal) segments.push(goal);
  const updated = typeof formatDate === 'function' ? formatDate() : null;
  if (updated) segments.push(`Updated ${updated}`);
  return segments.join(' · ');
};

const StackCard = ({
  stack,
  isInteractive = true,
  supplementMap = {},
  /**
   * Mobile folds the rows behind the header row (§8). Desktop is always
   * expanded — §8 again: "My stacks cards are fully expanded — no accordion."
   * The caller decides, because it is the breakpoint that decides.
   */
  collapsible = false,
  shareUrl,
  showShareField,
  onEdit,
  onView,
  onDelete,
  onTogglePublic,
  onShare,
  onShareFieldClose,
  onCopyToClipboard,
  formatDate,
  interactionStatus = null,
  /**
   * The already-resolved sentence for a check that could not run, or null
   * (STACK-542). The card renders words, never a decision: the host maps the
   * failure kind to a string through `Dashboard/copy.js`'s
   * `interactionFailureLine`, so both card types read from one place and this
   * shared component keeps no dashboard copy of its own.
   */
  interactionFailureLine = null,
  hasInteractions = false,
  supplementInteractions = {},
  userMedicationCount = 0,
  /**
   * Suppress the per-card disclaimer. Set by list surfaces that draw many
   * stacks — the dashboard's My stacks grid — which show one disclaimer for
   * the whole view instead of repeating it in every grid cell (STACK-517,
   * PR 326). The default *shows* it, so a new surface repeats the sentence
   * rather than losing it.
   */
  hideDisclaimer = false,
}) => {
  const [interactionModalOpen, setInteractionModalOpen] = useState(false);
  const [selectedInteractionData, setSelectedInteractionData] = useState(null);

  /**
   * Only the *mobile* accordion has a stored open/closed state. Whether the
   * rows are actually shown is derived, so desktop is always expanded no
   * matter what that state holds.
   *
   * This was `useState(!collapsible)` and that was a bug worth naming, because
   * `collapsible` tracks a media query and therefore changes under the user:
   * open the page on mobile (collapsed), rotate or resize to desktop, and the
   * stored `false` survived while the toggle that could undo it disappeared —
   * the supplements were unreachable until a reload. Deriving makes that state
   * unrepresentable rather than patching it with a sync effect.
   */
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const expanded = collapsible ? mobileExpanded : true;

  const supplements = Array.isArray(stack.supplements) ? stack.supplements : [];
  const visible = supplements.slice(0, MAX_ROWS);
  const overflow = supplements.length - visible.length;

  /**
   * The card-level risk level: the worst risk across its supplements. It is
   * computed here rather than read off the header so that the chip in the
   * always-visible header row means the same thing as the chips on the rows
   * it is standing in for while they are collapsed.
   */
  const cardRiskLevel = supplements.reduce((worst, supplement) => {
    const key = supplement.supplementId || supplement.key;
    const interaction = supplementInteractions[key];
    if (!interaction) return worst;
    const level = riskChipLevel({ interaction });
    if (!level) return worst;
    // Low is a finding we hold (STACK-528) and ranks below Medium. Absence
    // (`null`) is the clean signal — never treat an empty/errored list as
    // Low. `riskChipLevel` already returns null when has_interaction is false.
    const rank = { high: 3, medium: 2, low: 1 };
    return (rank[level] || 0) > (rank[worst] || 0) ? level : worst;
  }, null);

  /**
   * The display name for a supplement key, resolved the same way the rows
   * themselves resolve it: the shared map first, the stack's own stored name
   * second. Returns null rather than the raw key — a heading reading
   * "vitamin_d · Metformin" is worse than no label at all.
   */
  const supplementLabelFor = (key) =>
    supplementMap[key] ||
    supplements.find((supplement) => (supplement.supplementId || supplement.key) === key)
      ?.name ||
    null;

  const handleInteractionClick = () => {
    if (!hasInteractions || !supplementInteractions) return;

    const interactions = Object.entries(supplementInteractions).map(
      ([supplementKey, interactionData]) => ({
        medicationName:
          interactionData.medication_name ||
          `Medication (${interactionData.medication_in_rxcui})`,
        risk: interactionData.risk,
        reasoning: interactionData.reasoning,
        medication_in_rxcui: interactionData.medication_in_rxcui || null,
        supplementKey,
        // This modal's rows span several supplements when it is opened from
        // here, and they are labelled by medication — so two supplements
        // interacting with the same medication render as identical headings
        // and only the reasoning text tells them apart (Aravind, 2026-09-13).
        // The supplement-scoped callers pass no label; see the modal.
        supplementLabel: supplementLabelFor(supplementKey),
      })
    );

    const riskPriority = { High: 3, Medium: 2, Low: 1 };
    interactions.sort((a, b) => (riskPriority[b.risk] || 0) - (riskPriority[a.risk] || 0));

    setSelectedInteractionData({
      supplementName: stack.name || 'Stack',
      interactions,
      primaryInteraction: interactions.length > 0 ? interactions[0] : null,
    });
    setInteractionModalOpen(true);
  };

  const interactionCount = Object.keys(supplementInteractions || {}).length;

  const toggleExpanded = () => setMobileExpanded((open) => !open);

  const title = (
    <Box sx={{ minWidth: 0, flex: 1 }}>
      <Box
        component="h3"
        sx={{
          margin: 0,
          fontSize: '1.0625rem', // 17px/800, per §3
          fontWeight: 800,
          letterSpacing: '-0.02em',
          color: color.ink,
          lineHeight: 1.3,
        }}
      >
        {getDisplayName(stack)}
      </Box>
      <Box
        sx={{
          marginTop: '0.25rem',
          fontSize: '0.75rem',
          color: color.inkSoft,
          lineHeight: 1.5,
          ...tabularNums,
        }}
      >
        {buildMetaLine(stack, formatDate)}
      </Box>
    </Box>
  );

  /**
   * The header row.
   *
   * In collapsible mode the accordion toggle covers the **title only**, and
   * the risk chip sits beside it as a sibling — never inside it. `RiskChip`
   * renders a `ButtonBase`, so nesting it in the toggle would produce a
   * `<button>` inside a `<button>`: invalid markup that browsers reparent, and
   * a tap on the chip would both open the interaction modal and expand the
   * card. §8 requires that chip to be reachable at every breakpoint, so it has
   * to actually work when tapped.
   *
   * The chevron gets its own toggle so pointer users can still tap it, with
   * `tabIndex={-1}` so the keyboard sees exactly one control for one action
   * rather than two tab stops that do the same thing.
   */
  const headerRow = (
    <Box
      data-testid="stack-card-header"
      sx={{
        order: 0,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '0.75rem',
        width: '100%',
        padding: '1rem 1.125rem',
        textAlign: 'left',
      }}
    >
      {collapsible ? (
        <ButtonBase
          onClick={toggleExpanded}
          aria-expanded={expanded}
          sx={{
            display: 'block',
            flex: 1,
            minWidth: 0,
            minHeight: '2.75rem', // §8 hit target
            textAlign: 'left',
            '&:focus-visible': {
              outline: `${r(2)} solid ${color.accent}`,
              outlineOffset: r(2),
            },
          }}
        >
          {title}
        </ButtonBase>
      ) : (
        title
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
        {collapsible && (
          <ButtonBase
            onClick={toggleExpanded}
            tabIndex={-1}
            aria-hidden="true"
            sx={{
              width: '2.75rem',
              height: '2.75rem',
              borderRadius: radius.sm,
              color: color.inkMuted,
            }}
          >
            <ExpandMoreIcon
              sx={{
                transition: `transform ${motion.ui} ${motion.ease}`,
                transform: expanded ? 'rotate(180deg)' : 'none',
                [REDUCED]: { transition: 'none' },
              }}
            />
          </ButtonBase>
        )}
      </Box>
    </Box>
  );

  /**
   * Interaction risk, adjacent to the supplements it describes.
   *
   * It used to sit beside the title, which left it ambiguous which
   * supplements it referred to (Tanu, 2026-08-26). The approved renders put it
   * against the list — above it at 390px (4d), below it on desktop (4a, 4b,
   * 4e) — and that split is deliberate: on a phone the card can be collapsed
   * and risk should read before anything else, while on desktop the whole list
   * is visible and the chip summarises it. Reproduced with flex `order` so
   * there is one element in the DOM, not two.
   *
   * It sits OUTSIDE the Collapse in both cases, which is what §8 requires:
   * risk is never behind a tap, at any breakpoint.
   */
  /**
   * A check that could not run says so, instead of leaving the slot empty
   * (STACK-542). Empty is what a clean stack looks like, so silence here reads
   * as "no interactions found" off a check that never completed.
   *
   * Gated on `PROCESSING` as well as on the line itself so the spinner and the
   * failure can never draw together: while the check is still running there is
   * nothing to report yet, and a host that leaves a stale line on a re-check
   * cannot produce the pair.
   */
  const showInteractionFailure =
    Boolean(interactionFailureLine) && interactionStatus !== 'PROCESSING';

  const riskRow = isInteractive
    && (cardRiskLevel || interactionStatus === 'PROCESSING' || showInteractionFailure) && (
    <Box
      data-testid="stack-card-risk"
      sx={{
        order: { xs: 1, md: 3 },
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        paddingInline: '1.125rem',
        paddingBottom: '0.5rem',
      }}
    >
      {cardRiskLevel && (
        <RiskChip
          level={cardRiskLevel}
          count={interactionCount}
          onClick={handleInteractionClick}
        />
      )}
      {interactionStatus === 'PROCESSING' && (
        <Tooltip
          title={
            userMedicationCount > 0
              ? `Checking for interactions across ${supplements.length * userMedicationCount} supplement-drug combinations…`
              : 'Checking for interactions…'
          }
        >
          <CircularProgress size="1rem" sx={{ color: color.inkMuted }} />
        </Tooltip>
      )}
      {showInteractionFailure && (
        <Box
          data-testid="stack-card-interaction-failure"
          sx={{ display: 'flex', alignItems: 'flex-start' }}
        >
          {/* The info treatment `StackDisclaimer` uses, in muted ink: this is a
              state the member is told about, not a risk finding. No chip fill,
              no border and no accent — "Try again" is words, and green here
              would read as something to press. */}
          <InfoOutlinedIcon
            sx={{
              fontSize: '1rem',
              mr: r(6),
              color: color.inkMuted,
              mt: '0.0625rem',
              flexShrink: 0,
            }}
          />
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: color.inkMuted,
              lineHeight: 1.4,
            }}
          >
            {interactionFailureLine}
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Box
      data-testid="stack-card"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        backgroundColor: color.paper,
        border: `${r(1)} solid ${color.line}`,
        borderRadius: radius.lg,
        boxShadow: 'none', // §10: hairlines separate cards.
        transition: `border-color ${motion.micro} ${motion.ease}`,
        '&:hover': { borderColor: color.evergreen },
      }}
    >
      {/* The card's own 4px rule, flush to the top edge (Tanu, 2026-08-27) —
          the counterpart to the accent bar a recommended card carries, so the
          two card types read as siblings rather than as one decorated and one
          bare.
          
          It clips to the card's 16px radius because the container already sets
          `overflow: hidden`, so the bar needs no radius of its own, and it
          sits inside the 1px border and above the content padding. Flat fill,
          no gradient or shadow, and nothing ever sits on top of it — the title
          stays on paper below. `order: -1` because this card's other children
          carry explicit orders; without it the bar would sort against them
          rather than ahead of them.
          
          Decorative, so `aria-hidden`: the Private/Public text already says
          what the card is, and colour is never the only signal. */}
      <Box
        aria-hidden="true"
        data-testid="stack-card-bar"
        sx={{
          order: -1,
          height: '0.25rem',
          backgroundColor: color.evergreen,
          flexShrink: 0,
        }}
      />

      {headerRow}

      {riskRow}

      <Collapse
        in={expanded}
        timeout={collapsible ? undefined : 0}
        unmountOnExit={false}
        sx={{ order: 2 }}
      >
        {/* The rows are ONE grid, not a stack of flex rows, and that is what
            makes the doses line up (Tanu, 2026-08-29).

            They were `justify-content: space-between` with an auto-width,
            right-aligned dose box, so every dose ended flush right and *began*
            somewhere different — `0.8 mg`, `2 billion CFU`, `320 mg` and `5 g`
            each started at their own x. `textAlign: 'left'` alone does not fix
            that: the box still hugs the right edge, so the left edge still
            moves with the content.

            A grid fixes it at the container, where the problem actually is. One
            `max-content` column is measured across every row at once, so the
            dose column is exactly as wide as the longest dose in THIS card and
            every dose starts at the same x. The alternative the ticket offered
            — a fixed width — needs a number chosen for a worst case that
            varies per stack: too narrow wraps `2 billion CFU · 2×/day` and
            makes rows unequal heights (the thing Tanu rejected on 2026-08-27),
            too wide squeezes supplement names on a 390px phone for nothing.

            The name column is `minmax(0, 1fr)` and ellipsises, so a long dose
            is paid for by the name rather than by overflowing the card. */}
        <Box
          data-testid="supplement-rows"
          sx={{
            paddingInline: '1.125rem',
            paddingBottom: '0.5rem',
            display: 'grid',
            gridTemplateColumns: 'minmax(0, 1fr) max-content',
            columnGap: '0.75rem',
            alignItems: 'baseline',
          }}
        >
          {visible.map((supplement, index) => {
            const key = supplement.supplementId || supplement.key;
            // Frequency renders whenever it is available (Tanu, 2026-08-27).
            // It used to be gated on `dosageSource === 'pdf_verified'`, which
            // hid it for every stack the user typed themselves.
            const freqLabel = formatFrequencyCompact(supplement.dosageDetails?.frequency);
            const tooltipContent = buildDosageTooltip(supplement);
            const dosage = supplement.dosage ? getDisplayDosage(supplement) : null;

            // One line — "600 mg · 2×/day" — rather than the frequency
            // stacking beneath the dose (Tanu, 2026-08-27). Stacking made a row
            // with a frequency taller than one without, so no two rows in a
            // card lined up. Left-aligned as of 2026-08-29; the grid column
            // above is what gives every row the same starting x.
            const doseText = dosage ? `${dosage} ${supplement.unit || ''}`.trim() : '—';
            const dosageNode = (
              <Box
                data-testid="supplement-dose"
                sx={{
                  textAlign: 'left',
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  color: color.textBody,
                  whiteSpace: 'nowrap',
                  ...tabularNums,
                }}
              >
                {doseText}
                {freqLabel && (
                  <Box component="span" sx={{ color: color.inkMuted, fontWeight: 500 }}>
                    {` · ${freqLabel}`}
                  </Box>
                )}
              </Box>
            );

            // Each row contributes its two cells directly to the grid — a
            // wrapper Box per row would make the row the grid item and put the
            // dose column back inside it, one independent width per row, which
            // is the bug. The separator therefore goes on both cells rather
            // than on a row container.
            const rowRule = {
              paddingBlock: '0.5rem',
              borderTop: index === 0 ? 'none' : `${r(1)} solid ${color.lineSoft}`,
            };

            return (
              <React.Fragment key={key || index}>
                <Box
                  // The full name on hover, since it is the half that can now
                  // be cut. A supplement the reader cannot finish reading and
                  // cannot recover is a worse trade than the staggered doses
                  // this replaced.
                  title={supplementMap[key] || supplement.name}
                  sx={{
                    ...rowRule,
                    fontSize: '0.8125rem',
                    fontWeight: 500,
                    color: color.ink,
                    minWidth: 0,
                    // The name yields to the dose, not the other way round:
                    // a truncated name is still identifiable, a truncated
                    // dose is a different dose.
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {/* `key`, not `supplement.supplementId`: stacks stored
                      before the field was renamed carry only `key`, and
                      looking up the wrong one silently falls through to the
                      raw stored name instead of the canonical label.
                      `RecommendedStackCard` already resolves it this way. */}
                  {supplementMap[key] || supplement.name}
                </Box>
                <Box sx={rowRule}>
                  {tooltipContent ? (
                    <Tooltip title={tooltipContent} arrow placement="top">
                      <Box sx={{ cursor: 'help' }}>{dosageNode}</Box>
                    </Tooltip>
                  ) : (
                    dosageNode
                  )}
                </Box>
              </React.Fragment>
            );
          })}

          {overflow > 0 && (
            <Box
              sx={{
                // Spans both columns — it is a line about the card, not a row.
                gridColumn: '1 / -1',
                paddingTop: '0.5rem',
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: color.inkSoft,
                ...tabularNums,
              }}
            >
              + {overflow} more
            </Box>
          )}
        </Box>
      </Collapse>

      {/* The card draws its own supplement rows rather than going through
          `SupplementsTable`, so where it is the terminal surface for a stack it
          has to carry the disclaimer itself.

          On the dashboard's My stacks grid it is not that surface, and the grid
          passes `hideDisclaimer` (STACK-541, Tanu 2026-09-14): the card is a
          summary that opens into View and Edit, and both carry the line through
          `SupplementsTable`. The branch stays for any host that shows a
          StackCard as the last stop — without it, such a host would show a
          stack with no provider line anywhere.

          Deliberately *outside* the `Collapse`, unlike the pre-redesign card
          where the rows were always visible. Below 640px the rows fold into an
          accordion, and a disclaimer inside it would be hidden on exactly the
          viewport where the card is most often read collapsed — the same
          reasoning §8 applies to the risk chip. */}
      {!hideDisclaimer && stack.supplements?.length > 0 && (
        <StackDisclaimer sx={{ order: 3, paddingInline: '1.125rem', paddingBottom: '0.5rem' }} />
      )}

      {isInteractive && (
        <Box
          sx={{
            order: 4,
            marginTop: 'auto',
            padding: '0.75rem 1.125rem',
            backgroundColor: color.surface,
            borderTop: `${r(1)} solid ${color.line}`,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.5rem',
              flexWrap: 'wrap',
            }}
          >
            {onTogglePublic && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                <Box
                  component="span"
                  sx={{
                    fontSize: '0.75rem',
                    fontWeight: stack.isPublic ? 600 : 400,
                    // `linkHover`, the same green View and Edit use, so the
                    // footer speaks with one voice (Tanu, 2026-08-28). It was
                    // `evergreen` — a structural token for nav, footers and
                    // dark surfaces, not for small text beside text controls.
                    color: stack.isPublic ? color.linkHover : color.inkSoft,
                  }}
                >
                  {stack.isPublic ? 'Public' : 'Private'}
                </Box>
                <Switch
                  checked={Boolean(stack.isPublic)}
                  inputProps={{ 'aria-label': stack.isPublic ? 'Make private' : 'Make public' }}
                  onChange={() => {
                    logAnalyticsEvent('toggle_stack_public', {
                      stack_id: stack.id,
                      new_status: !stack.isPublic,
                    });
                    onTogglePublic(stack.id, stack.isPublic);
                    if (!stack.isPublic && onShare) onShare(stack.id);
                  }}
                  size="small"
                  // `accent`, the system's action colour, scoped to this one
                  // switch (Tanu, 2026-08-29). It inherited MUI's
                  // `primary.main` — `evergreen` — and so read as a third
                  // green beside a `linkHover` label and `linkHover` View /
                  // Edit buttons.
                  //
                  // Scoped here rather than fixed at `primary.main`, which
                  // would repaint every filled button in the product and
                  // breaks `theme/tokens.test.js`'s pin on that value.
                  //
                  // A switch is a fill, not text, so `accent` is right and this
                  // does not touch the 2026-08-26 accent-as-text ruling: at
                  // 3.44:1 on `paper` it clears the 3.0:1 WCAG asks of a UI
                  // control's boundary, while green *text* stays `linkHover`.
                  //
                  // Checked state only. The unchecked track and thumb are MUI's
                  // neutral greys and stay that way — a Private stack has no
                  // action colour on it.
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: color.accent,
                      '&:hover': { backgroundColor: alpha(color.accent, 0.08) },
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: color.accent,
                    },
                  }}
                />
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              {[
                onView && { label: 'View', event: 'view_stack_click', run: () => onView(stack) },
                onEdit && { label: 'Edit', event: 'edit_stack_click', run: () => onEdit(stack.id) },
                onDelete && {
                  label: 'Delete',
                  event: 'delete_stack_click',
                  run: () => onDelete(stack),
                },
              ]
                .filter(Boolean)
                .map((action) => (
                  <Button
                    key={action.label}
                    onClick={() => {
                      logAnalyticsEvent(action.event, { stack_id: stack.id });
                      action.run();
                    }}
                    sx={{
                      minHeight: '2.75rem',
                      minWidth: '2.75rem',
                      paddingInline: '0.5rem',
                      // Accent is the action colour, but at 3.3:1 on this
                      // footer it fails AA as *text*. `linkHover` is the
                      // system's own darker green — 5.0:1 here, same role,
                      // no new value (Tanu, 2026-08-26). Accent stays on
                      // filled CTAs, where `accentInk` carries the contrast.
                      // Delete keeps `destructive` — red means this destroys
                      // something, per STACK-501.
                      color: action.label === 'Delete' ? color.destructive : color.linkHover,
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      textTransform: 'none',
                      '&:focus-visible': {
                        outline: `${r(2)} solid ${color.accent}`,
                        outlineOffset: r(2),
                      },
                    }}
                  >
                    {action.label}
                  </Button>
                ))}
            </Box>
          </Box>

          {/* §6: the share row sits directly under the switch when the stack is
              Public. The × hides the row from view only — it does not revoke
              the link and does not change the public state. */}
          {stack.isPublic && showShareField && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                marginTop: '0.5rem',
              }}
            >
              <TextField
                value={shareUrl || ''}
                size="small"
                fullWidth
                inputProps={{ 'aria-label': 'Share link', readOnly: true }}
                InputProps={{
                  sx: {
                    fontSize: '0.8125rem',
                    backgroundColor: color.paper,
                    borderRadius: radius.sm,
                    '& input': { textOverflow: 'ellipsis' },
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: color.line },
                  },
                }}
                onFocus={(e) => e.target.select()}
              />
              <Button
                onClick={() => {
                  logAnalyticsEvent('copy_share_link', { stack_id: stack.id });
                  onCopyToClipboard(shareUrl);
                }}
                startIcon={<CopyIcon sx={{ fontSize: '1rem' }} />}
                sx={{
                  minHeight: '2.75rem',
                  paddingInline: '0.75rem',
                  borderRadius: radius.md,
                  border: `${r(1)} solid ${color.line}`,
                  // Matches View / Edit, as above.
                  color: color.linkHover,
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  flexShrink: 0,
                }}
              >
                Copy
              </Button>
              <IconButton
                onClick={() => onShareFieldClose(stack.id)}
                aria-label="Hide share link"
                sx={{
                  // §8 names the share × explicitly as a 44×44 hit target.
                  width: '2.75rem',
                  height: '2.75rem',
                  color: color.inkSoft,
                  '& .MuiSvgIcon-root': { fontSize: '1rem' },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          )}
        </Box>
      )}

      <InteractionDetailsModal
        open={interactionModalOpen}
        onClose={() => setInteractionModalOpen(false)}
        supplementName={selectedInteractionData?.supplementName}
        interactions={selectedInteractionData?.interactions || []}
        primaryInteraction={selectedInteractionData?.primaryInteraction}
      />

    </Box>
  );
};

export default StackCard;
